/**
 * Maps an app's own AppStore listing (schema "phidias/appstore-listing/v1") onto the portal's download-page config.
 *
 * The app owner writes the words; this function only lays them out. It never rewrites, shortens or adds text: every
 * title, line, limit, caption and step is copied verbatim, `known_limits` is always carried through, and the only
 * strings it supplies itself are two fixed badge labels and CSS class names.
 */
import type { AppDownloadConfig, FeatureConfig, IconKey } from './appDownloadConfigs.ts'

export interface AppListing {
  schema: string
  app_id: string
  name: string
  tagline: string
  description: string
  audience?: string
  features: { title: string; line: string; evidence?: string[] }[]
  known_limits?: string[]
  install?: string[]
  platform?: string
  ios?: string
  version: string
  version_code?: number
  sha256?: string
  icon?: string
  screenshots?: { path: string; caption: string }[]
}

// Icons are decoration only; they carry no claim. Assigned by position so the mapping is predictable.
const FEATURE_ICONS: IconKey[] = ['shield', 'camera', 'lock', 'star', 'zap']

const basename = (p: string): string => p.split('/').pop() || p

export function listingToConfig(listing: AppListing, assetBase: string, source: string): AppDownloadConfig {
  const lastStep = listing.install && listing.install.length > 0 ? listing.install[listing.install.length - 1] : ''
  const features: FeatureConfig[] = listing.features.map((f, i) => ({
    iconKey: FEATURE_ICONS[i % FEATURE_ICONS.length],
    title: f.title,
    description: f.line,
  }))

  return {
    appId: listing.app_id,
    name: listing.name,
    tagline: listing.tagline,
    description: listing.description,
    theme: {
      titleGradient: 'bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent',
      heroBg: 'bg-gradient-to-br from-amber-900/50 via-orange-900/30 to-transparent',
      iconShadow: 'shadow-amber-500/30',
      iconFallbackGradient: 'from-amber-500 via-orange-500 to-yellow-500',
      featureBorder: 'hover:border-amber-500/50',
      accentText: 'text-amber-400',
      stepBg: 'bg-amber-500',
    },
    // The listing's icon is shown as-is; nothing is substituted for it.
    iconPath: `${assetBase}/${basename(listing.icon || 'icon-512.png')}`,
    downloadPath: `/download/mskai-apps/android/${listing.app_id}`,
    iosManifestPath: `/api/manifest/${listing.app_id}/ios`,
    defaultVersion: listing.version,
    defaultFileSize: 'N/A',
    features,
    trustBadges: [
      { value: listing.version, label: 'Release build' },
      { value: 'Android', label: listing.platform || '' },
    ],
    androidFinalStep: lastStep,
    iosFinalStep: listing.ios || '',
    unknownSourceNote: '',
    completionMessage: lastStep,
    audience: listing.audience,
    featuresHeading: 'What it does',
    knownLimits: listing.known_limits ? [...listing.known_limits] : [],
    installSteps: listing.install ? [...listing.install] : undefined,
    platformNote: listing.platform,
    iosStatement: listing.ios,
    screenshots: (listing.screenshots || []).map((s) => ({
      src: `${assetBase}/screenshots/${basename(s.path)}`,
      caption: s.caption,
    })),
    listingVersion: listing.version,
    defaultVersionCode: listing.version_code,
    sha256: listing.sha256,
    contentSource: source,
  }
}
